'use client';

import { TZDate } from '@date-fns/tz';
import { format, startOfDay } from 'date-fns';
import { useMemo, useState } from 'react';
import { useGetApiMatchesMatchesCalendar } from '@/api/backoffice/matches/matches';
import type { MatchDetailDto } from '@/api/backoffice-schemas';
import { Button } from '@/components/button';
import Calendar from '@/components/calendar';
import { MatchCard } from '@/components/cards/MatchCard';
import SkeletonEvent from '@/components/skeletons/event';
import { useGetGameFromRouteParams } from '@/hooks/useGetGameFromParams.hook';
import { cn } from '@/utils/cn';
import { mapGameFilterToVideoGameName } from '@/utils/gameMappers';

type AdvancedMatchesFeedWidgetProps = {
  type: 'finished' | 'not_started';
  className?: string;
};

export const AdvancedMatchesFeedWidget = ({
  type,
  className,
}: AdvancedMatchesFeedWidgetProps) => {
  const { game } = useGetGameFromRouteParams();

  const [date, setDate] = useState<Date>(() => new Date());

  const gameParam = useMemo(() => mapGameFilterToVideoGameName(game), [game]);

  const { data, isLoading } = useGetApiMatchesMatchesCalendar({
    Status: type,
    BeginAt: format(date, 'yyyy-MM-dd'),
    Game: gameParam,
    PageSize: 5,
  });

  return (
    <div
      className={cn(
        'flex w-fill flex-col gap-5 rounded-lg bg-medium-gray p-4',
        className
      )}
    >
      <div className="flex flex-row items-center justify-start">
        <h2 className="font-bold text-gray">
          {type === 'finished' ? 'Match results' : 'Upcoming events'}
        </h2>
      </div>
      <div className="flex flex-col gap-3.5 md:gap-3">
        <Calendar
          onChange={(newDate) => {
            setDate(new TZDate(startOfDay(newDate)));
          }}
          type={type === 'finished' ? 'past' : 'future'}
        />
        <div className="flex flex-col gap-2.5">
          {isLoading &&
            Array.from({ length: 5 }, (_, index) => (
              <SkeletonEvent key={`skeleton-${Date.now()}-${index}`} />
            ))}
          {!isLoading &&
            data?.data.items &&
            data.data.items.length > 0 &&
            data.data.items.map((item: MatchDetailDto) => (
              <MatchCard
                data={item}
                key={item.id || `event-${item.externalId}`}
              />
            ))}
          {!isLoading &&
            (!data?.data.items || data.data.items.length === 0) && (
              <div className="flex flex-1 flex-col gap-2 rounded-lg bg-component-bg px-3 py-5">
                <p className="font-medium text-gray text-sm leading-[18px]">
                  {type === 'finished'
                    ? 'No matches played yet.'
                    : 'No more matches today.'}
                </p>
                <p className="font-normal text-[10px] text-gray leading-3">
                  {type === 'finished'
                    ? 'Check out later today.'
                    : 'Check out tomorrows schedule.'}
                </p>
              </div>
            )}
        </div>
      </div>
      <div className="flex md:justify-end">
        <Button
          as="link"
          className="w-full md:w-auto"
          href="/matches"
          size="lg"
          variant="outline"
        >
          More Matches
        </Button>
      </div>
    </div>
  );
};
